import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';

const STATUS_COLORS = {
    paid: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    pending: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    expired: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
};

export default function AdminPayments() {
    const [data, setData] = useState({ rows: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [providerFilter, setProviderFilter] = useState('all');

    const load = async () => {
        setLoading(true);
        try {
            const params = { limit: 100 };
            if (statusFilter !== 'all') params.status = statusFilter;
            if (providerFilter !== 'all') params.provider = providerFilter;
            const { data } = await api.get('/admin/payments', { params });
            setData(data);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, providerFilter]);

    const totals = data.rows.reduce((acc, r) => {
        if (r.status !== 'paid') return acc;
        const k = (r.currency || 'inr').toLowerCase();
        acc[k] = (acc[k] || 0) + Number(r.amount || 0);
        return acc;
    }, {});

    return (
        <div className="p-6 lg:p-10 space-y-6" data-testid="admin-payments">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="overline">Payments</div>
                    <h1 className="font-serif text-3xl text-parchment mt-2">{data.total} transactions</h1>
                    <div className="text-sm text-slate-500 mt-1">
                        Paid in view: ₹{(totals.inr || 0).toLocaleString('en-IN')}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-36 bg-elevated border-marigold/15 text-parchment rounded-sm" data-testid="status-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-surface border-marigold/15 text-parchment">
                            <SelectItem value="all">All statuses</SelectItem>
                            <SelectItem value="paid">Paid</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="expired">Expired</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={providerFilter} onValueChange={setProviderFilter}>
                        <SelectTrigger className="w-36 bg-elevated border-marigold/15 text-parchment rounded-sm" data-testid="provider-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-surface border-marigold/15 text-parchment">
                            <SelectItem value="all">All providers</SelectItem>
                            <SelectItem value="razorpay">Razorpay</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <Card className="rounded-sm bg-elevated border-marigold/10 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-left text-slate-500 text-xs uppercase tracking-wider border-b border-marigold/10">
                            <tr>
                                <th className="px-4 py-3">Date</th>
                                <th className="px-4 py-3">User</th>
                                <th className="px-4 py-3">Provider</th>
                                <th className="px-4 py-3">Type</th>
                                <th className="px-4 py-3">Amount</th>
                                <th className="px-4 py-3">Credits</th>
                                <th className="px-4 py-3">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan="7" className="px-4 py-6 text-slate-500">Loading…</td></tr>}
                            {!loading && data.rows.length === 0 && (
                                <tr><td colSpan="7" className="px-4 py-6 text-slate-500" data-testid="payments-empty">No transactions.</td></tr>
                            )}
                            {data.rows.map(r => (
                                <tr key={r.id} className="border-b border-marigold/5 hover:bg-obsidian/40" data-testid={`payment-row-${r.id}`}>
                                    <td className="px-4 py-3 text-slate-500 text-xs">{r.created_at?.slice(0, 16).replace('T', ' ')}</td>
                                    <td className="px-4 py-3 text-slate-300">{r.user_email || r.user_id}</td>
                                    <td className="px-4 py-3 text-slate-400 capitalize">{r.provider}</td>
                                    <td className="px-4 py-3 text-slate-400">
                                        {r.kind === 'consultation' ? 'Consultation' : (r.pack_id || 'Credit pack')}
                                    </td>
                                    <td className="px-4 py-3 text-parchment">
                                        {r.currency?.toLowerCase() === 'inr' ? '₹' : '$'}{Number(r.amount || 0).toFixed(2)}
                                    </td>
                                    <td className="px-4 py-3 text-marigold">{r.credits || '—'}</td>
                                    <td className="px-4 py-3">
                                        <Badge className={`${STATUS_COLORS[r.status] || STATUS_COLORS.pending} hover:bg-transparent`}>{r.status}</Badge>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    );
}
